import type { MessageContent } from "@tg/shared";
import { formatFullDateTime, type TimeZonePref } from "./datetime.js";

// Текстовое представление сообщений «как в оригинальном Telegram» при
// копировании выделенного (Ctrl+C) и экспорте чата:
//
//   [28.08.2026 16:31] Blessed: Тест
//
// Один форматтер на оба сценария, чтобы буфер обмена и .txt совпадали.
// Дата — через lib/datetime.ts в поясе саппорта (единая точка tz).

export interface ExportableMessage {
  outgoing: boolean;
  senderName?: string | undefined;
  internalAuthorName?: string | undefined;
  content?: MessageContent | undefined;
  // Для строк, где content уже развёрнут в текст на бэкенде (экспорт).
  text?: string | undefined;
  occurredAt: string;
}

export interface ExportFormatOptions {
  // Имя нашего аккаунта для исходящих. Если нет — «Вы».
  selfName?: string | null | undefined;
  timeZone?: TimeZonePref | undefined;
}

const MEDIA_LABELS: Record<string, string> = {
  photo: "Фото",
  video: "Видео",
  document: "Файл",
  voice: "Голосовое",
  videoNote: "Кружок",
  sticker: "Стикер"
};

/** Текст сообщения для экспорта: медиа помечаем квадратными скобками. */
export const exportContentText = (content: MessageContent): string => {
  switch (content.kind) {
    case "text":
      return (content.text ?? "").trim();
    case "photo":
    case "video":
    case "document": {
      const label = `[${MEDIA_LABELS[content.kind] ?? "Медиа"}${
        content.kind === "document" && content.fileName ? ` ${content.fileName}` : ""
      }]`;
      const caption = content.caption?.trim();
      return caption ? `${label} ${caption}` : label;
    }
    case "voice":
    case "videoNote": {
      // Есть расшифровка — копируем её, как она видна на экране; без
      // расшифровки остаётся метка.
      const transcript = content.transcript?.trim();
      return transcript ? transcript : `[${MEDIA_LABELS[content.kind]}]`;
    }
    case "sticker":
      return content.emoji ? `[Стикер ${content.emoji}]` : "[Стикер]";
    default:
      return "[Сообщение]";
  }
};

export const exportStamp = (occurredAt: string, timeZone?: TimeZonePref): string =>
  // formatFullDateTime даёт «28.08.2026, 16:31» — запятую убираем ради
  // формата оригинального TG.
  formatFullDateTime(occurredAt, timeZone).replace(", ", " ");

export const exportAuthorName = (
  message: ExportableMessage,
  selfName: string | null | undefined
): string => {
  if (message.outgoing) {
    return selfName?.trim() || "Вы";
  }
  return message.senderName?.trim() || message.internalAuthorName?.trim() || "Собеседник";
};

export const formatMessageLine = (
  message: ExportableMessage,
  options: ExportFormatOptions
): string | null => {
  // Для голосовых/кружков текст модели — это метка «Голосовое сообщение»,
  // а расшифровка лежит в content.transcript: при копировании выделенных
  // сообщений в буфер уезжала метка вместо расшифровки с экрана.
  const transcriptContent =
    message.content &&
    (message.content.kind === "voice" || message.content.kind === "videoNote") &&
    message.content.transcript?.trim()
      ? message.content
      : null;
  const text = transcriptContent
    ? exportContentText(transcriptContent)
    : message.text !== undefined
      ? message.text.trim()
      : message.content
        ? exportContentText(message.content)
        : "";
  if (text.length === 0) {
    return null;
  }
  const author = exportAuthorName(message, options.selfName);
  return `[${exportStamp(message.occurredAt, options.timeZone)}] ${author}: ${text}`;
};

/** Строки без текста пропускаются; порядок сохраняется. */
export const formatMessageLines = (
  messages: readonly ExportableMessage[],
  options: ExportFormatOptions
): string =>
  messages
    .map((message) => formatMessageLine(message, options))
    .filter((line): line is string => line !== null)
    .join("\n");

/** «Имя Фамилия» из identity аккаунта, либо @username, либо null. */
export const selfDisplayName = (
  identity: { firstName?: string; lastName?: string; username?: string } | null | undefined
): string | null => {
  if (!identity) {
    return null;
  }
  const full = [identity.firstName, identity.lastName]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0)
    .join(" ");
  if (full.length > 0) {
    return full;
  }
  return identity.username ? `@${identity.username}` : null;
};
